// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * ADR-079 — the mobile side of a durable Amy turn: ONE module-level store
 * that owns the turn's phase (lib/amyTurn.ts, the byte-identical twin rule),
 * the on-device pending-turn record (SecureStore, enough to ask the server
 * after a restart — never the full prompt), the coalesced status reads, and
 * the 2 s poll while the server says "running". No React inside; the screen
 * and the root watcher subscribe.
 *
 * Why a store and not screen state: the founder's report ("she stops
 * reasoning when I switch apps") is exactly the case where the Amy screen
 * may be unmounted while the turn finishes. The root watcher resumes from
 * AppState and shows the local banner; the screen renders whatever phase it
 * finds when it comes back.
 */
import { useSyncExternalStore } from "react"
import type { AmyTurnResponse } from "@bitebalance/contracts"
import { emptyAttemptProgress, type AttemptProgress } from "./amyInterruption"
import {
  POLL_INTERVAL_MS,
  decodePendingTurn,
  encodePendingTurn,
  isPendingRecordFresh,
  reduceTurn,
  statusEventForMissing,
  statusEventFromTurn,
  type PendingTurnRecord,
  type StreamLossDecision,
  type TurnEvent,
  type TurnPhase,
} from "./amyTurn"

/** The one read this store needs — the recovery endpoint, status only. */
export interface TurnReader {
  getAmyTurn: (id: string) => Promise<AmyTurnResponse>
}

/** Structural twin of the SecureStore binding (lib/secureStoreKV.ts) so tests can inject a map. */
export interface KeyValueStore {
  get: (key: string) => string | null
  set: (key: string, value: string) => void
  remove: (key: string) => void
}

export const PENDING_TURN_KEY_PREFIX = "bb_amy_turn_"

/** expo-secure-store keys only allow alphanumeric/`.`/`-`/`_` (same rule as amySession.ts). */
function sanitizeKeyPart(raw: string): string {
  return raw.replace(/[^A-Za-z0-9._-]/g, "_")
}

export function pendingTurnKey(userKey: string): string {
  return `${PENDING_TURN_KEY_PREFIX}${sanitizeKeyPart(userKey)}`
}

/** Sign-out hygiene (#106): the pending record carries a preview of the user's message — it never outlives the session on a shared device. */
export function clearPendingTurn(store: KeyValueStore, userKey: string): void {
  const key = pendingTurnKey(userKey)
  try {
    store.set(key, "")
    store.remove(key)
  } catch {
    /* best-effort */
  }
}

/** A resume that finds no row while the stream is still live re-checks once after this — the runtime registers the turn just after `turn_started`, so an immediate app switch can read before the row exists. */
export const REGISTRATION_GRACE_MS = 3_000

export interface TurnState {
  phase: TurnPhase
  /** The pending record while a turn may still be running or its answer is not yet on screen. */
  record: PendingTurnRecord | null
  /** Whether the Amy screen is focused — what "answered while you were away" is measured against. */
  screenFocused: boolean
  /** The turn a local banner was shown for (once per turn, this app run). */
  bannerShownFor: string | null
  /** The preview of a message the server never received — quoted back under the composer. */
  notReceivedPreview: string | null
  /** An answer landed while away and the screen has not shown it yet (cleared by ackAnswered). */
  awayAnswerPending: boolean
}

const IDLE_STATE: TurnState = {
  phase: { kind: "idle" },
  record: null,
  screenFocused: false,
  bannerShownFor: null,
  notReceivedPreview: null,
  awayAnswerPending: false,
}

/** A 404 means the server has never seen the turn; anything else is the API being unreachable — never a licence to resend. */
export function classifyTurnReadError(err: unknown): "unregistered" | "unreachable" {
  const status =
    typeof err === "object" && err !== null ? (err as { status?: unknown }).status : undefined
  return status === 404 ? "unregistered" : "unreachable"
}

const WAITING_KINDS: ReadonlySet<TurnPhase["kind"]> = new Set([
  "live",
  "reconnecting",
  "stillWorking",
])

export function createAmyTurnStore(options: { now?: () => number } = {}) {
  const now = options.now ?? (() => Date.now())
  let state: TurnState = IDLE_STATE
  let kv: KeyValueStore | null = null
  let key: string | null = null
  let inflight: Promise<void> | null = null
  /** Bumped by every new read: an older read (e.g. a resume waiting out the registration grace) never dispatches a stale verdict. */
  let readGeneration = 0
  let pollTimer: ReturnType<typeof setTimeout> | null = null
  let lastReader: TurnReader | null = null
  const listeners = new Set<() => void>()

  const emit = () => {
    for (const listener of listeners) listener()
  }

  const persist = (record: PendingTurnRecord | null) => {
    if (!kv || !key) return
    try {
      if (record) kv.set(key, encodePendingTurn(record))
      else {
        // set-then-remove: SecureStore's delete is async-only; an empty overwrite is effective immediately.
        kv.set(key, "")
        kv.remove(key)
      }
    } catch {
      /* storage unavailable — the in-memory phase still drives this app run */
    }
  }

  const stopPolling = () => {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  const schedulePoll = () => {
    if (pollTimer || !lastReader) return
    pollTimer = setTimeout(() => {
      pollTimer = null
      dispatch({ type: "poll_tick", now: now() })
      if (state.phase.kind === "stillWorking" && lastReader) void resume(lastReader)
    }, POLL_INTERVAL_MS)
  }

  function dispatch(event: TurnEvent): void {
    const prev = state.phase
    const next = reduceTurn(prev, event)
    if (next === prev) return
    let record = state.record
    let notReceivedPreview = state.notReceivedPreview
    let awayAnswerPending = state.awayAnswerPending
    // The first successful status read replaces the client's send time with the server's clock.
    if (event.type === "status" && record && event.turnId === record.turnId && event.startedAt) {
      record = { ...record, startedAt: event.startedAt, expiresAt: event.expiresAt }
      persist(record)
    }
    switch (next.kind) {
      case "idle":
        record = null
        notReceivedPreview = null
        awayAnswerPending = false
        persist(null)
        stopPolling()
        break
      case "failed":
        if (next.reason === "unregistered")
          notReceivedPreview = record?.promptPreview ?? notReceivedPreview
        record = null
        awayAnswerPending = false
        persist(null)
        stopPolling()
        break
      case "abandoned":
        record = null
        awayAnswerPending = false
        persist(null)
        stopPolling()
        break
      case "answered":
        // Answered on a live stream: the text is already on screen. Answered while away: the record
        // stays until the screen has refetched history (ackAnswered) or the banner took over.
        awayAnswerPending = next.answeredWhileAway
        if (!next.answeredWhileAway) {
          record = null
          persist(null)
        }
        stopPolling()
        break
      case "stillWorking":
        break
      default:
        stopPolling()
    }
    state = { ...state, phase: next, record, notReceivedPreview, awayAnswerPending }
    if (next.kind === "stillWorking") schedulePoll()
    emit()
  }

  function beginTurn(input: {
    turnId: string
    conversationId: string
    runtimeSessionId: string
    promptPreview: string
    now: number
  }): void {
    const record: PendingTurnRecord = {
      v: 1,
      turnId: input.turnId,
      conversationId: input.conversationId,
      runtimeSessionId: input.runtimeSessionId,
      startedAt: new Date(input.now).toISOString(),
      expiresAt: null,
      promptPreview: input.promptPreview.slice(0, 120),
    }
    persist(record)
    stopPolling()
    const alreadyLive = state.phase.kind === "live" && state.phase.turnId === input.turnId
    state = {
      ...state,
      record,
      notReceivedPreview: null,
      awayAnswerPending: false,
      phase: alreadyLive
        ? state.phase
        : reduceTurn(state.phase, { type: "send", turnId: input.turnId, now: input.now }),
    }
    emit()
  }

  function rehydrate(record: PendingTurnRecord): void {
    state = {
      ...state,
      record,
      phase: {
        kind: "reconnecting",
        turnId: record.turnId,
        startedAt: Date.parse(record.startedAt),
        attempt: 2,
      },
    }
    emit()
  }

  function safeGet(store: KeyValueStore, storeKey: string): string | null {
    try {
      return store.get(storeKey)
    } catch {
      return null
    }
  }

  /** Bind the device store for this user; a fresh pending record left by an earlier app run is picked up (cold), never over a live turn. */
  function bindStorage(store: KeyValueStore, userKey: string): void {
    const nextKey = pendingTurnKey(userKey)
    if (kv === store && key === nextKey) return
    if (key && key !== nextKey) {
      stopPolling()
      state = IDLE_STATE
      emit()
    }
    kv = store
    key = nextKey
    if (state.phase.kind !== "idle") return
    const record = decodePendingTurn(safeGet(store, nextKey))
    if (!record) return
    if (!isPendingRecordFresh(record, now())) {
      persist(null)
      return
    }
    rehydrate(record)
  }

  const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

  /**
   * One status read. `origin: "resume"` reads (foreground, focus, poll, cold start) never own the outcome
   * of a LIVE stream: the in-flight invoke does — a missing row while live is re-checked once after the
   * registration grace before it may mean "unregistered", and an unreachable API while live is ignored.
   */
  async function readStatus(
    reader: TurnReader,
    turnId: string,
    f: { attempt: 1 | 2; progress: AttemptProgress; origin: "resume" | "stream_loss" }
  ): Promise<void> {
    lastReader = reader
    const generation = ++readGeneration
    const stale = () => generation !== readGeneration
    for (let pass = 0; ; pass += 1) {
      try {
        const turn = await reader.getAmyTurn(turnId)
        if (stale()) return
        dispatch(
          statusEventFromTurn(turn, {
            screenFocused: state.screenFocused,
            now: now(),
            attempt: f.attempt,
            progress: f.progress,
          })
        )
        return
      } catch (err) {
        if (stale()) return
        const missing = classifyTurnReadError(err)
        const live = state.phase.kind === "live" && state.phase.turnId === turnId
        if (f.origin === "resume" && live) {
          if (missing === "unreachable") return
          if (pass === 0) {
            await wait(REGISTRATION_GRACE_MS)
            if (stale() || !(state.phase.kind === "live" && state.phase.turnId === turnId)) return
            continue
          }
        }
        dispatch(
          statusEventForMissing(turnId, missing, {
            screenFocused: state.screenFocused,
            now: now(),
            attempt: f.attempt,
            progress: f.progress,
          })
        )
        return
      }
    }
  }

  /** One read in flight at a time is what callers share; a NEWER read supersedes an older one (its verdict is dropped as stale). */
  function startRead(
    reader: TurnReader,
    turnId: string,
    f: { attempt: 1 | 2; progress: AttemptProgress; origin: "resume" | "stream_loss" }
  ): Promise<void> {
    const read: Promise<void> = readStatus(reader, turnId, f).finally(() => {
      if (inflight === read) inflight = null
    })
    inflight = read
    return read
  }

  /** Ask the server about the pending turn (foreground, focus, poll). Idempotent and coalesced: one read in flight, no-op without a turn. */
  function resume(reader: TurnReader): Promise<void> {
    if (inflight) return inflight
    const record = state.record
    if (record && !isPendingRecordFresh(record, now())) {
      dispatch({ type: "reset" })
      return Promise.resolve()
    }
    if (state.phase.kind === "idle" && record) rehydrate(record)
    const phase = state.phase
    if (!WAITING_KINDS.has(phase.kind) || phase.kind === "idle") return Promise.resolve()
    // A resume has no attempt of its own: it never resends (attempt 2, nothing received).
    return startRead(reader, phase.turnId, {
      attempt: 2,
      progress: emptyAttemptProgress(),
      origin: "resume",
    })
  }

  /**
   * The screen's invoke() settled without an error. A legacy runtime sends no terminal event — a settled
   * live stream is the answer; a `turn_duplicate` left the phase reconnecting — the server's status
   * settles it (the row exists by definition), so nothing can stay "thinking" until the next foreground.
   */
  async function settleAfterStream(reader: TurnReader, turnId: string): Promise<void> {
    const phase = state.phase
    if (phase.kind === "live" && phase.turnId === turnId) {
      dispatch({ type: "stream_end", turnId })
      return
    }
    if (phase.kind === "reconnecting" && phase.turnId === turnId) await resume(reader)
  }

  /** The screen's stream died: read the server's status and decide — resume what it knows, retry once only when it never saw the turn and nothing came back, else interrupt. */
  async function afterStreamLoss(
    reader: TurnReader,
    f: { turnId: string; attempt: 1 | 2; progress: AttemptProgress }
  ): Promise<StreamLossDecision> {
    dispatch({ type: "stream_error", turnId: f.turnId, attempt: f.attempt })
    // The screen's own loss supersedes any resume still waiting out the registration grace.
    await startRead(reader, f.turnId, {
      attempt: f.attempt,
      progress: f.progress,
      origin: "stream_loss",
    })
    const phase = state.phase
    if (phase.kind === "live") return { action: "retry" }
    if (
      phase.kind === "failed" &&
      (phase.reason === "interrupted" ||
        phase.reason === "unregistered" ||
        phase.reason === "unreachable")
    )
      return { action: "interrupt" }
    return { action: "resume" }
  }

  /** The screen rendered the answer that arrived while away — nothing is pending any more. */
  function ackAnswered(): void {
    if (!state.record && !state.awayAnswerPending) return
    persist(null)
    state = { ...state, record: null, awayAnswerPending: false }
    emit()
  }

  /** The root watcher took over with a banner (once per turn): the record is done; the in-memory phase still renders the affordance this app run. */
  function markBannerShown(turnId: string): void {
    persist(null)
    state = { ...state, bannerShownFor: turnId, record: null }
    emit()
  }

  function setScreenFocused(focused: boolean): void {
    if (state.screenFocused === focused) return
    state = { ...state, screenFocused: focused }
    emit()
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  function getState(): TurnState {
    return state
  }

  return {
    subscribe,
    getState,
    dispatch,
    beginTurn,
    bindStorage,
    resume,
    settleAfterStream,
    afterStreamLoss,
    ackAnswered,
    markBannerShown,
    setScreenFocused,
  }
}

export type AmyTurnStore = ReturnType<typeof createAmyTurnStore>

export const amyTurnStore: AmyTurnStore = createAmyTurnStore()

export function useAmyTurnState(): TurnState {
  return useSyncExternalStore(amyTurnStore.subscribe, amyTurnStore.getState, amyTurnStore.getState)
}

export function useAmyTurnPhase(): TurnPhase {
  return useAmyTurnState().phase
}
